import { z } from "zod"

/**
 * POST /api/v1/account/delete (T322). `confirm` is the account email as the user typed it; the
 * server compares it case-insensitively after trimming. Strict: unknown keys are rejected.
 * Both /account/delete and /account/restore answer 204 with no body.
 */
export const accountDeleteRequestSchema = z.strictObject({
  confirm: z.string().min(1).max(320),
  /**
   * T1381 (Apple 5.1.1(v)): the fresh Sign in with Apple authorization code from a re-authentication done at
   * deletion time. Single use, valid 5 minutes at Apple, never stored: the server exchanges it for a refresh token
   * and revokes that token at once. Omit it for an account that did not sign in with Apple.
   */
  apple_authorization_code: z.string().min(1).max(2048).optional(),
})

export type AccountDeleteRequest = z.infer<typeof accountDeleteRequestSchema>

/** Outcome of the Apple token revocation (T1381). Single source of truth for the values. */
export const APPLE_REVOKE_RESULTS = ["revoked", "failed", "not_applicable"] as const
export type AppleRevokeResult = (typeof APPLE_REVOKE_RESULTS)[number]

/**
 * Answer of POST /account/delete when the request carried `apple_authorization_code` (200 with this body); without
 * the code the answer stays 204 with no body, as before. Lenient (response schema): `apple_revoke` is a plain string,
 * so a value a newer server adds never fails an older client; check it with `APPLE_REVOKE_RESULTS.includes`.
 * `failed` and `not_applicable` do not stop the deletion: the app tells the user to remove it in iOS Settings.
 */
export const accountDeleteResponseSchema = z.object({
  apple_revoke: z.string(),
})

export type AccountDeleteResponse = z.infer<typeof accountDeleteResponseSchema>

/** Days between the delete request and the purge (PRD R1.5). */
export const ACCOUNT_DELETION_GRACE_DAYS = 30

/**
 * Data retention after a paid plan lapses (T784, D-RETENTION-3M / D-RETENTION-TIMELINE,
 * docs/design/data-retention-and-deletion.md). Day 0 is the lapse date (UTC calendar day).
 * Every retention number lives here; nothing else may hard-code them.
 */
/** Days from lapse to soft delete (all access cut): the export window, about 3 months. */
export const RETENTION_GRACE_DAYS = 90
/** Reminders go out this many days before the soft delete (day 30/60/76/83/89 after lapse). */
export const RETENTION_REMINDER_DAYS_BEFORE = [60, 30, 14, 7, 1] as const
/** Days between soft delete and hard delete: the window to restore by paying. */
export const SOFT_DELETE_RESTORE_DAYS = 14
/** Longest a backup copy lives after the hard delete. Wording and checks only, no code action. */
export const BACKUP_MAX_DAYS = 35
/** A never-paid free account this many months inactive enters the same flow (day 0 = that date). */
export const FREE_INACTIVITY_MONTHS = 6

/** Why an organisation is in the retention flow. */
export const RETENTION_BASES = ["paid_lapse", "free_inactivity"] as const
export type RetentionBasis = (typeof RETENTION_BASES)[number]

/**
 * Retention state a client sees on `GET /api/v1/me` (T1084). The server derives it from the
 * retention clock (`apps/web/apps/account/retention-phase.ts`):
 * - `lapsed`: day 0..29, export window, no reminder yet
 * - `reminder`: day 30..89, a reminder is due (the 7-day push is day 83)
 * - `soft_deleted`: day 90 or later, access cut; restore window open until the purge date
 * The response schema keeps `state` a plain string (see `meRetentionSchema`), so a state a newer
 * server adds never fails an older client: it shows nothing for a value it does not know.
 */
export const RETENTION_ME_STATES = ["lapsed", "reminder", "soft_deleted"] as const
export type RetentionMeState = (typeof RETENTION_ME_STATES)[number]

/**
 * `retention` on `GET /api/v1/me` and on each `organisations[]` entry (T1084, T822): null or
 * absent while the organisation is active. All dates are UTC calendar dates `YYYY-MM-DD`.
 * Lenient on purpose (stored/response schema): plain strings, no length or enum limits, so an old
 * client never fails to decode `/me`. Use `RETENTION_BASES` / `RETENTION_ME_STATES` with
 * `includes` to decide whether a value is a known one.
 *
 * - `basis`: why the clock runs (`paid_lapse` = plan lapse, `free_inactivity`); absent for an
 *   active Member, accountant or admin of a team (T1281), who get only `state`, `accessEndsOn`, `purgeOn`
 * - `lapsedOn`: day 0; absent like `basis` for a Member, accountant or admin
 * - `accessEndsOn`: day `RETENTION_GRACE_DAYS` (all access cut)
 * - `purgeOn`: hard delete; exact once soft deleted, the earliest possible date before that
 */
export const meRetentionSchema = z.object({
  basis: z.string().optional(),
  state: z.string(),
  lapsedOn: z.string().optional(),
  accessEndsOn: z.string(),
  purgeOn: z.string(),
})

export type MeRetention = z.infer<typeof meRetentionSchema>
